/**
 * Suppression for Inertia visits that were aborted rather than answered.
 *
 * Inertia's router ends its request chain by firing a cancelable
 * `inertia:exception` event and then re-rejecting the error unless a listener
 * cancels it. Nothing listened, so every failed visit became an unhandled
 * promise rejection and Sentry reported it via `onunhandledrejection` — 70k
 * `Error: Network Error` events over three and a half years (WEB-SWINGU-3P).
 *
 * 97% of those came from `Mobile Safari UI/WKWebView` against a flat long tail
 * of URLs, with almost nothing from Android's webview or desktop. That spread
 * rules out flaky cell service, which would hit every platform alike: these are
 * in-flight visits killed when the native iOS app tears down or navigates away
 * from the webview. The document is already going away, so there is no user to
 * inform and nothing for us to retry.
 *
 * Only transport failures are swallowed. Anything that reached the server, and
 * anything thrown while resolving or swapping the page component — a stale
 * chunk, a component-level throw — still surfaces exactly as it does today.
 */

/**
 * Whether an Inertia visit error means the request never got a response.
 *
 * Axios sets `request` on every error it raises after opening the XHR, and
 * `response` only once bytes came back (see `enhanceError` in the axios copy
 * that `@inertiajs/inertia` vendors). The pair therefore distinguishes "no
 * answer" from "an answer we didn't like", and leaves errors that never came
 * from axios — which carry neither field — untouched.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
const isAbortedVisitError = (error) =>
    Boolean(error && error.request && !error.response);

/**
 * Register the `inertia:exception` listener that drops aborted visits.
 *
 * Returning `false` from an `Inertia.on` callback calls `preventDefault()` on
 * the event, which is what stops the router from re-rejecting. Returning
 * `undefined` leaves the default behaviour intact.
 *
 * @param {{on: (event: string, callback: (event: CustomEvent) => unknown) => VoidFunction}} inertia
 * @returns {VoidFunction} Unsubscribe callback.
 */
const suppressAbortedVisitErrors = (inertia) =>
    inertia.on("exception", (event) => {
        if (isAbortedVisitError(event.detail?.exception)) {
            return false;
        }
    });

export { isAbortedVisitError, suppressAbortedVisitErrors };
